import CircularProgress from "@mui/material/CircularProgress";
import DialogContentText from "@mui/material/DialogContentText";
import { ReactNode, useEffect, useState } from "react";

import { ConfirmDialog, ResourceInfo } from "../ConfirmDialog";
import { FundedSubmission, useCancelImpactSubmissions } from "./usePayment";

/**
 * Renders the list of submissions a payment funds. Every SubmissionPayment row
 * has isRequired defaulting to true with no write path that sets it false, so
 * the "(required)" distinction was vacuous — it was dropped to avoid a
 * misleading always-on red flag.
 */
function FundedSubmissionsList({ submissions }: { submissions: FundedSubmission[] }) {
  return (
    <DialogContentText component="div" sx={{ mb: 2 }}>
      This payment funds the following submission(s). Cancelling it may leave a required filing
      unpaid and delinquent:
      <ul style={{ marginTop: 8, marginBottom: 0 }}>
        {submissions.map((s) => (
          <li key={s.id}>
            {s.title ?? "Untitled submission"} — {s.status}
          </li>
        ))}
      </ul>
    </DialogContentText>
  );
}

type CancelPaymentConfirmDialogProps = {
  open: boolean;
  /** The payment being cancelled; its funded-submissions impact is fetched when
   * the dialog opens. Undefined defers the fetch until an id is available. */
  paymentId: string | undefined;
  header: string;
  /** Trailing explanatory copy shown after the funded-submissions warning. */
  info?: string | ReactNode;
  /** Resource rows (e.g. the payment id) shown above the warning. */
  resources?: ResourceInfo[];
  confirmText?: string | ReactNode;
  cancelText?: string | ReactNode;
  /** True while the cancel mutation is in flight. */
  loading?: boolean;
  /**
   * Set to true when cancelling a report-associated payment (ReportPayments
   * context). Report payments don't link to SubmissionPayment rows, so the
   * pre-cancel impact query will always return empty. Without this flag the
   * empty state is indistinguishable from "checked, nothing funded" — with it
   * the dialog renders an explicit "not linked to any submission" notice so
   * staff understand the check isn't broken.
   */
  isReportPayment?: boolean;
  onConfirm: () => void | Promise<unknown>;
  onClose: () => void;
  onCancel?: () => void;
};

/**
 * The single entry point for confirming a payment cancellation. It owns the
 * pre-cancel impact fetch and the funded-submissions warning so no cancel call
 * site can forget to wire them — adding a new cancel button means rendering this
 * component, and the warning comes for free.
 */
export function CancelPaymentConfirmDialog({
  open,
  paymentId,
  header,
  info,
  resources,
  confirmText,
  cancelText,
  loading,
  isReportPayment = false,
  onConfirm,
  onClose,
  onCancel,
}: CancelPaymentConfirmDialogProps) {
  const [loadImpact, impact] = useCancelImpactSubmissions();

  // hasFired tracks whether loadImpact has been called for the current open
  // cycle. Stored as state so setting it triggers a re-render and hasResolved
  // is computed correctly on that pass. Reset to false when the dialog closes
  // so a subsequent open starts fresh and confirm stays blocked until the new
  // query resolves.
  const [hasFired, setHasFired] = useState(false);

  // Fire the impact fetch when the dialog opens for a payment. Owning this here
  // (rather than at each call site) is what makes the warning non-forgettable.
  useEffect(() => {
    if (open && paymentId) {
      setHasFired(true);
      loadImpact(paymentId);
    }
    if (!open) {
      setHasFired(false);
    }
    // oxlint-disable-next-line exhaustive-deps -- fire on the open transition for a payment; loadImpact is a fresh closure each render and including it would re-fire.
  }, [open, paymentId]);

  // The query has resolved for this open cycle when it has been fired AND is
  // no longer loading. Closes the idle render frame between open=true and the
  // first useEffect completing the query.
  const hasResolved = hasFired && !impact.loading;

  const hasFunded = impact.data.length > 0;
  // Report payments don't have SubmissionPayment rows, so the impact query
  // will always return empty for this path. Show a distinct notice so staff
  // can distinguish "structurally no submissions" from "checked, nothing funded".
  const showNotLinked = isReportPayment && !impact.loading && !impact.error && !hasFunded;

  const warningContent = (
    <>
      {impact.loading && (
        <DialogContentText component="div" sx={{ mb: 2, display: "flex", alignItems: "center" }}>
          <CircularProgress size={16} sx={{ mr: 1 }} />
          Checking which submissions this payment funds…
        </DialogContentText>
      )}
      {!impact.loading && impact.error && (
        <DialogContentText
          component="div"
          sx={{ mb: 2, color: "error.main" }}
          data-testid="funded-submissions-error"
        >
          {impact.error} Cancellation is blocked until the check completes successfully.
        </DialogContentText>
      )}
      {!impact.loading && !impact.error && hasFunded && (
        <FundedSubmissionsList submissions={impact.data} />
      )}
      {showNotLinked && (
        <DialogContentText component="div" sx={{ mb: 2 }} data-testid="not-linked-to-submission">
          This payment isn&apos;t linked to any submission.
        </DialogContentText>
      )}
    </>
  );

  // Block confirm if: (a) the query is in flight, (b) it errored, or (c) it
  // has not yet resolved for this open cycle (closes the idle render frame
  // between open=true and the first useEffect firing and completing the query).
  const blockConfirm = impact.loading || !!impact.error || !hasResolved;

  return (
    <ConfirmDialog
      open={open}
      header={header}
      resources={resources}
      warningContent={warningContent}
      info={info}
      confirmText={confirmText}
      cancelText={cancelText}
      loading={loading}
      disableConfirm={blockConfirm}
      onConfirm={onConfirm}
      onClose={onClose}
      onCancel={onCancel}
    />
  );
}
